import { useSelector } from 'react-redux';
import {
  getLeftProjects,
  getProjectsIsFetching,
} from '../../../apps/main/rootReducer';
import { ProjectId, ProjectTabViews } from '../../../models/Types';
import { useMemo } from 'react';
import useProjectsContext from './useProjectsContext';
import useCompaniesContext from '../../companies/hooks/useCompaniesContext';

interface ContactSearchProps {
  searchTerm: string;
  type: ProjectTabViews;
  archivedProjectsHidden?: boolean;
}

export const useProjectSearch: (
  props: ContactSearchProps
) => [ProjectId[], boolean] = (props) => {
  const { type, searchTerm, archivedProjectsHidden } = props;

  const { allProjects, myProjects, favorites } = useProjectsContext();

  const isFetching = useSelector(getProjectsIsFetching);

  const { getCompanyById } = useCompaniesContext();

  const leftProjects = useSelector(getLeftProjects);

  const projects = useMemo(() => {
    return (
      type === 'all'
        ? allProjects
        : type === 'myProjects'
          ? myProjects
          : favorites
    ).filter((project) => {
      if (!project) {
        return false;
      }
      const { projectId, isArchived } = project;
      if (archivedProjectsHidden && isArchived) {
        return false;
      }
      return !leftProjects.includes(projectId);
    });
  }, [
    allProjects,
    myProjects,
    favorites,
    type,
    archivedProjectsHidden,
    leftProjects,
  ]);

  return useMemo(() => {
    if (!searchTerm || searchTerm === '') {
      return [projects.map(({ projectId }) => projectId), isFetching];
    }
    const filteredProjectIds = projects
      .filter((project) => {
        const subsidiary = getCompanyById(project.subsidiaryId);
        const company = getCompanyById(project.companyId);

        const projectInformation = `${project.number} ${project.shortName} ${project.name} ${project.eMailPrefix} ${project.eMailSuffix}`;
        const subsidiaryInformation = `${subsidiary?.shortName ?? ''} ${
          subsidiary?.fullName ?? ''
        } ${subsidiary?.fullName2 ?? ''} `;
        const companyInformation = `${company?.shortName ?? ''} ${
          company?.fullName ?? ''
        } ${company?.fullName2 ?? ''} `;

        const s = projectInformation
          .concat(subsidiaryInformation)
          .concat(companyInformation)
          ?.toLocaleLowerCase()
          ?.replace(/[^a-zA-Z0-9_-]+/g, ' ')
          ?.replace(/ /g, '');

        const searchTermArray = searchTerm
          ?.toLocaleLowerCase()
          ?.replace(/[^a-zA-Z0-9_-]+/g, ' ')
          ?.split(' ');

        let numberOfMatches = 0;
        searchTermArray.forEach((searchTerm) => {
          if (s.includes(searchTerm)) {
            numberOfMatches++;
          }
        });

        if (numberOfMatches === searchTermArray.length) {
          return true;
        } else {
          return false;
        }
      })
      .map(({ projectId }) => projectId);

    return [filteredProjectIds, isFetching];
  }, [projects, getCompanyById, isFetching, searchTerm]);
};

export default useProjectSearch;
